import { memo, useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";
import Select from "react-select";
import { cn } from "~/utils/tailwind";
import { yellow } from "tailwindcss/colors";
export interface ISelectBoxProps {
  id?: string;
  name: string;
  dataBlockId?: string;
  label?: string;
  disabled?: boolean;
  hint?: string;
  className?: string;
  isRequired?: boolean;
  showLabel?: boolean;
  hidden?: boolean;
  onFocus?: any;
  showErrorMessage?: boolean;
  options?: OptionsProps[];
  placeholder?: string;
  onChange?: (option: any) => void;
  enableBackground?: boolean;
  onOptionSelect?: (option: any) => void;
}

interface OptionsProps {
  key: string | number;
  value: string;
}

const SearchSelectBoxField = ({
  id,
  name,
  dataBlockId,
  label,
  disabled,
  hint,
  className,
  isRequired = false,
  showLabel = true,
  hidden,
  onFocus,
  showErrorMessage = true,
  options,
  placeholder,
  enableBackground = false,
  onOptionSelect,
}: ISelectBoxProps) => {
  const { control } = useFormContext();
  const {
    field: { onChange, onBlur, value },
    fieldState,
  } = useController({ control, name, defaultValue: null });

  const formattedOptions = useMemo(() => {
    return options
      ?.sort((a, b) => {
        const aValue = isNaN(Number(a.value)) ? a.value : Number(a.value);
        const bValue = isNaN(Number(b.value)) ? b.value : Number(b.value);

        if (aValue === bValue) {
          return 0;
        }

        return aValue < bValue ? -1 : 1;
      })
      .map((option) => ({
        value: option.key,
        label: option.value,
      }));
  }, [options]);

  const selectedOption = useMemo(() => {
    const option = formattedOptions?.find((option) => option.value === value) || null;
    if (option) {
      return option
    } else {
      if (value) {
        onChange(null);
      }
      return null;
    }
  }, [formattedOptions, value]);
  return (
    <div className="relative" hidden={hidden}>
      {showLabel && (
        <label
          htmlFor={name}
          className="absolute text-md text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white px-2"
        >
          <span className={cn(fieldState.error && "text-red-700")}>
            {label} {isRequired && <span className="text-red-500">*</span>}
          </span>
        </label>
      )}
      <Select
        id={id ?? name}
        aria-label={dataBlockId}
        styles={{
          control: (base) => ({
            ...base,
            backgroundColor: enableBackground ? yellow[200] : "",
          }),
        }}
        classNamePrefix="react-select"
        className={cn(
          "block w-full text-xs ",
          { "bg-gray-100 cursor-not-allowed": disabled },
          className,
          fieldState.error && "border-red-700 border-solid border-2"
        )}
        isDisabled={disabled}
        onFocus={onFocus}
        onChange={(option) => {
          onChange(option?.value || null);

          if (onOptionSelect) {
            onOptionSelect(option);
          }
        }}
        onBlur={onBlur}
        value={selectedOption}
        options={formattedOptions}
        placeholder={placeholder}
        isClearable
      />
      {fieldState?.error?.message && showErrorMessage && (
        <span className="text-red-700 px-1 text-xs">
          {fieldState?.error?.message}
        </span>
      )}
      {hint && <span className="px-2 text-xs">{hint}</span>}
    </div>
  );
};

export default memo(SearchSelectBoxField);
